import { t } from '../i18n';
import { ProductCardProps, DisplayStatus } from '@teko/ecommerce';
import {
  ProductCardProps as MobileProductCardProps,
  ProductPriceProps,
} from '@teko/ecommerce/mobile';
import { thoundsandDelimiter, getMoneyValueUnit, TagProps } from '@teko/ui';
import { isWithinInterval } from 'date-fns';
import { CartOrderLine, FlashSale } from 'types';
import get from 'lodash.get';
import {
  getDiscountPercent,
  getEnv,
  checkExpiryDateTimestamp,
  isValidPrice,
} from './index';
import { BannerEntity, DiscoveryProduct, Cart, ProductInfo } from '../types';
import { CartContextState } from '../contexts';
import { needConfirm18YearsOld } from './cart';
// backward compatible
export { getProductUrl } from '@teko/discovery';
import { getProductUrl } from '@teko/discovery';
export const getOrderExplain = ({
  orderValueMin,
  orderValueMax,
}: any): string => {
  const text = t('cho đơn hàng');
  const min = orderValueMin ? getMoneyValueUnit(orderValueMin) : undefined;
  const max = orderValueMax ? getMoneyValueUnit(orderValueMax) : undefined;
  if (min && max)
    if (min.unit === max.unit)
      return t('{{text}} từ {{MinValue}} - {{MaxValue}}{{MinUnit}}', {
        text: text,
        MinValue: min.value,
        MaxValue: max.value,
        MinUnit: min.unit,
      });
    else
      return t(
        '{{text}} từ {{MinValue}}{{MinUnit}} - {{MaxValue}}{{MaxUnit}}',
        {
          text: text,
          MinValue: min.value,
          MinUnit: min.unit,
          MaxValue: max.value,
          MaxUnit: max.unit,
        },
      );
  if (min)
    return t('{{text}} từ {{MinValue}}{{MinUnit}}', {
      text: text,
      MinValue: min.value,
      MinUnit: min.unit,
    });
  if (max)
    return t('{{text}} từ {{MaxValue}}{{MaxUnit}}', {
      text: text,
      MaxValue: max.value,
      MaxUnit: max.unit,
    });
  return t('cho tất cả các đơn hàng');
};
export const getProductStatus = (
  needManageStock: boolean,
  totalAvailable: number,
  status: string,
  isBundle: boolean,
): number => {
  if (
    ((status === 'hang_ban' || status === 'hang_sap_het') &&
      (needManageStock === false || totalAvailable > 0)) ||
    isBundle
  )
    return DisplayStatus.BUYABLE;
  if (
    ((status === 'hang_trung_bay' || status === 'hang_dat_truoc') &&
      (needManageStock === false || totalAvailable > 0)) ||
    (needManageStock &&
      totalAvailable <= 0 &&
      status !== 'hang_khac' &&
      status !== 'ngung_kinh_doanh')
  )
    return DisplayStatus.CONTACT;
  return DisplayStatus.NONE;
};
export const convertProduct = (product: any): ProductCardProps => {
  const { flashSales, combination } = product;
  const {
    status,
    price,
    orderCouponPromotions,
    defaultPromotions,
    selectionPromotions,
  } = combination || {};
  const { sellPrice, supplierSalePrice } = price || {};
  let gifts = [];
  const icons = [];
  // Hiển thị icon freeship nếu đơn hàng lớn hơn hoặc bằng 500k
  if (sellPrice >= 500000)
    icons.push({ name: 'freeshipping-color', color: '#61c94d', size: 22 });
  if (defaultPromotions) {
    gifts = defaultPromotions.reduce((acc: any, cur: any) => {
      return [...acc, ...cur.benefit.items];
    }, []);
  }
  const defaultSelectedPromotion =
    selectionPromotions &&
    selectionPromotions.length > 0 &&
    selectionPromotions[0];
  let productMoney = undefined;
  let productCoupon = undefined;
  let bestPrice = undefined;
  if (defaultSelectedPromotion) {
    gifts = [...gifts, ...get(defaultSelectedPromotion, 'benefit.items', [])];
    switch (defaultSelectedPromotion.type) {
      case 'coupon':
        productCoupon = {
          discountType: get(
            defaultSelectedPromotion,
            'benefit.money.0.discountType',
          ),
          money: get(defaultSelectedPromotion, 'benefit.money.0.money'),
          percent: get(defaultSelectedPromotion, 'benefit.money.0.percent'),
          coupon: get(defaultSelectedPromotion, 'condition.coupon'),
          maxDiscount: get(
            defaultSelectedPromotion,
            'benefit.money.0.maxDiscount',
          ),
          orderValueMax: get(
            defaultSelectedPromotion,
            'benefit.money.0.orderValueMax',
          ),
          orderValueMin: get(
            defaultSelectedPromotion,
            'benefit.money.0.orderValueMin',
          ),
          price: get(defaultSelectedPromotion, 'price'),
          type: 'product',
        };
        break;
      case 'product':
        if (
          get(combination, 'price.sellPrice') -
            get(defaultSelectedPromotion, 'price.sellPrice') >
          0
        ) {
          productMoney = {
            discount:
              get(combination, 'price.sellPrice', 0) -
              get(defaultSelectedPromotion, 'price.sellPrice', 0),
            price: get(defaultSelectedPromotion, 'price.sellPrice', 0),
            promotionId: defaultSelectedPromotion.id,
          };
        }
        break;
    }
    bestPrice = {
      promotionId: defaultSelectedPromotion.id,
      price: get(defaultSelectedPromotion, 'price.sellPrice', 0),
    };
  }
  // ordercoupon promotions
  const { discountType, money, percent, maxDiscount } =
    get(
      orderCouponPromotions ||
        [].sort(
          (couponPromotion1: any, couponPromotion2: any) =>
            couponPromotion2.id - couponPromotion1.id,
        ),
      '0.benefit.money.0',
    ) || {};
  const { coupon, orderValueMin, orderValueMax } =
    (orderCouponPromotions && get(orderCouponPromotions, '0.condition')) || {};
  const orderCoupon =
    (coupon &&
      ({
        discountType,
        money,
        percent,
        coupon,
        maxDiscount,
        orderValueMax,
        orderValueMin,
        price: undefined,
        type: 'order',
      } as any)) ||
    undefined;
  const displayCoupon = productCoupon || orderCoupon;
  const listedPrice =
    supplierSalePrice > sellPrice ? supplierSalePrice : undefined;
  const useNewProductUrlFormat =
    getEnv('USE_NEW_PRODUCT_URL_FORMAT') === 'true';
  return {
    link: {
      as: useNewProductUrlFormat
        ? `/${product.url}--s${product.sku}`
        : `/${product.url}-s${product.sku}.html`,
      href: { pathname: '/products/[sku]', query: { sku: product.sku } },
      passHref: true,
    },
    sku: product.sku,
    ...(flashSales &&
      flashSales.length && {
        flashsale: {
          endAt: new Date(flashSales[0].definition.endedAt),
          value: flashSales[0].used,
          total: flashSales[0].total,
        },
      }),
    image: {
      src:
        get(product, 'images.0.url') ||
        `/api/products/${product.sku}/default_image`,
      alt: product.name,
    },
    title: {
      name: product.displayName || product.name,
      ...(status && {
        status: {
          value: status.status,
          color: status.color,
        },
      }),
    },
    totalAvailableByStocks:
      get(product, 'totalAvailableByStocks', []).find(
        (stock: any) => stock.type === 'Biz',
      )?.total || 0,
    price: {
      price: get(product, 'promotionPrices.bestPrice') || sellPrice || 0,
      listedPrice: listedPrice,
      discount:
        getDiscountPercent(sellPrice, listedPrice) &&
        `${getDiscountPercent(sellPrice, listedPrice)}%`,
    },
    icons: icons,
    ...(displayCoupon &&
      displayCoupon.coupon &&
      (displayCoupon.type == 'product' ||
        (displayCoupon.type == 'order' &&
          (!productMoney || (productMoney && productMoney.discount == 0)))) && {
        // chỉ hiện coupon khi có coupon của sản phẩm hoặc có (coupon order nhưng ko có khuyến mãi giảm tiền lựa chọn cho sản phẩm)
        coupon: {
          tag: {
            children: displayCoupon.coupon,
            variant: 'secondary',
          },
          detail: {
            text:
              displayCoupon.type == 'order'
                ? getOrderExplain(displayCoupon)
                : t('Chỉ còn') + ' ',
            highlight:
              displayCoupon.type == 'order'
                ? t('Giảm {{var0}}', {
                    var0: displayCoupon.percent
                      ? `${displayCoupon.percent}% `
                      : `${thoundsandDelimiter(displayCoupon.money)}đ`,
                  })
                : t('{{ThoundsandDelimiter(displayCouponPriceSellPrice)}}đ', {
                    'ThoundsandDelimiter(displayCouponPriceSellPrice)':
                      thoundsandDelimiter(displayCoupon.price.sellPrice),
                  }),
            reverse: displayCoupon.type == 'order' ? true : false,
          },
        },
      }),
    ...(gifts.length > 0 && {
      gifts: {
        label: t('Tặng ngay'),
        images: gifts.map((gift: any) => ({
          src: gift.imageURL || `/api/products/${gift.sku}/default_image`,
          alt: gift.name,
          sku: gift.sku,
        })),
      },
    }),
    ...(bestPrice && {
      bestPrice: bestPrice,
    }),
    ...(productMoney &&
      productMoney.discount > 0 &&
      (!displayCoupon || (displayCoupon && displayCoupon.type == 'order')) && {
        money: {
          discount: t('{{ThoundsandDelimiter(productMoneyDiscount)}}đ', {
            'ThoundsandDelimiter(productMoneyDiscount)': thoundsandDelimiter(
              productMoney.discount,
            ),
          }),
          price: t('{{ThoundsandDelimiter(productMoneyPrice)}}đ', {
            'ThoundsandDelimiter(productMoneyPrice)': thoundsandDelimiter(
              productMoney.price,
            ),
          }),
        },
      }),
    displayStatus: getProductStatus(
      get(product, 'status.needManageStock', true),
      get(product, 'totalAvailableByStocks', []).find(
        (item: any) => item.type === 'Biz',
      )?.total || 0,
      get(product, 'status.sale'),
      product?.isBundle,
    ),
    needManageStock: get(product, 'status.needManageStock'),
  };
};
export const getProductWithBestPrice = (product: any): ProductCardProps => {
  const newProduct = convertProduct(product);
  if (get(newProduct, 'bestPrice.price')) {
    const { price, bestPrice } = newProduct;
    price.listedPrice = price.listedPrice || price.price;
    price.price = bestPrice.price;
    price.discount = `${Math.round(
      ((price.listedPrice - price.price) / price.listedPrice) * 100,
    )}%`;
    newProduct.price = price;
  }
  return newProduct;
};
export const getOrderDetail = (cartOrder: Cart) => {
  return {
    cartItems: cartOrder.orderLines.map((orderLine) => {
      return {
        sku: orderLine.sku,
        name: orderLine.displayName || orderLine.name,
        price: orderLine.price,
        quantity: orderLine.quantity,
        rowTotal: orderLine.price * orderLine.quantity,
      };
    }),
    discountValue: cartOrder.totalDiscount || 0,
    shippingFee: cartOrder.shippingFee,
    total: cartOrder.grandTotal || 0,
    subTotal: cartOrder.subTotal || 0,
  };
};
export const converOrderProduct = (orderLines: any) => {
  const items: any = [];
  if (!!orderLines.length) return [];
  orderLines.map((orderLine: any) => {
    const orderProduct = {
      sku: orderLine.sku,
      name: orderLine.name,
      quantity: orderLine.quantity,
    };
    items.push(orderProduct);
  });
  return items;
};
export const isNotRefundableProduct = (attributeSet: any): boolean => {
  return get(attributeSet, 'id') === 40;
};
export const pvHasConditionToShipAndInstall = (
  product: ProductInfo,
): boolean => {
  const shipmentAndInstallationCategoryCodes = [
    '08-N002-02',
    '08-N002-03',
    '08-N002-04',
    '08-N003-01',
    '08-N003-02',
    '08-N003-03',
    '08-N003-08',
    '08-N002-01',
  ];
  const itemCategories = product.categories?.map(({ code }) => code);
  for (let i = 0; i < itemCategories?.length; i += 1) {
    for (let j = 0; j < shipmentAndInstallationCategoryCodes.length; j += 1) {
      if (
        itemCategories[i]
          .toUpperCase()
          .startsWith(shipmentAndInstallationCategoryCodes[j])
      ) {
        return true;
      }
    }
  }
  return false;
};
export const vnshopHasConditionToShipAndInstall = (
  product: ProductInfo,
): boolean => {
  const LIMIT_SHIPPING_SELLER_CATS = [
    '018-001',
    '018-003',
    '018-004',
    '018-005',
    '018-006',
    '018-007',
  ];
  const sellerCats = product.masterCategories.map(({ code }) => code);
  return sellerCats.some((catCode) =>
    LIMIT_SHIPPING_SELLER_CATS.includes(catCode),
  );
};
export const getComboPromotion = (
  promotions: any,
  comboskus: Array<string>,
): any => {
  const gifts: any = [];
  promotions.forEach((promotion: any) => {
    promotion.applyOn.map((lineItem: any) => {
      if (comboskus.includes(lineItem.lineItemId)) {
        if (!gifts.includes(...promotion.gifts)) gifts.push(...promotion.gifts);
      }
    });
  });
  return {
    gifts: gifts,
  };
};
export const passQuantityCheck = (
  needManageStock: boolean,
  totalAvailable: number,
  quantity: number | undefined,
): boolean => {
  if (needManageStock && quantity >= totalAvailable) return false;
  return true;
};
export const convertStringToDate = (value: string): Date => {
  return new Date(new Date().toISOString().substring(0, 10).concat(' ', value));
};
export const getProductFlashSaleEndTime = (flashSales: FlashSale[]): Date => {
  if (
    !flashSales ||
    get(flashSales, '0.definition.type.code', null) !== 'GOLDEN_TIME'
  )
    return null;
  const startedAt = new Date(flashSales[0].definition.startedAt);
  const endedAt = new Date(flashSales[0].definition.endedAt);
  const current = new Date();
  if (current > endedAt || current < startedAt) {
    return null;
  }
  let end = null;
  if (get(flashSales, '0.definition.timeRanges', []).length) {
    flashSales[0].definition.timeRanges.forEach((time: any) => {
      const startTime = convertStringToDate(time.start);
      const endTime = convertStringToDate(time.end);
      if (isWithinInterval(current, { start: startTime, end: endTime })) {
        end = endTime;
      }
    });
  } else {
    if (current.getDate() === endedAt.getDate()) {
      end = endedAt;
    } else {
      end = convertStringToDate('23:59:59');
    }
  }
  return end;
};
export const getFlashSaleMinEndTimeDiscovery = (products: any[]): Date => {
  if (!products) return null;
  let minEndTime = new Date();
  minEndTime.setHours(23, 59, 59);
  products.forEach((product: any) => {
    const { flashSales } = product;
    if (get(flashSales, '0.endTimestampSec')) {
      const endTime = new Date(parseInt(flashSales[0].endTimestampSec) * 1000);
      if (endTime < minEndTime) minEndTime = endTime;
    }
  });
  return minEndTime;
};
export const getFrame = (
  tags: string[],
  frames: BannerEntity[],
): string | undefined => {
  const ids: number[] = [];
  tags.forEach((el) => {
    const matchReg = el.match(/^frame_(\d+)_.+/);
    if (matchReg && matchReg[1] && !isNaN(parseInt(matchReg[1]))) {
      ids.push(parseInt(matchReg[1]));
    }
  });
  const frame = frames.find(
    (fr) =>
      ids.includes(fr.id) && checkExpiryDateTimestamp(fr.startDate, fr.endDate),
  );
  return frame ? frame.image : undefined;
};
//Convert discovery price format to component price format
export const convertPrice = (product: DiscoveryProduct): ProductPriceProps => {
  const { prices } = product;
  const price = parseInt(get(prices, '0.latestPrice'));
  // const sellPrice = parseInt(get(prices, '0.sellPrice'));
  const supplierRetailPrice = parseInt(get(prices, '0.supplierRetailPrice'));
  const listedPrice = price > supplierRetailPrice ? null : supplierRetailPrice;
  const discount = getDiscountPercent(price, listedPrice);
  return {
    price: isNaN(price) ? undefined : price,
    listedPrice: isNaN(listedPrice) ? undefined : listedPrice,
    discount: isNaN(discount) ? undefined : discount,
  };
};
export const convertProductCardFn = (
  product: DiscoveryProduct,
  frames?: BannerEntity[],
  cart?: CartContextState,
): MobileProductCardProps => {
  const sku = get(product, 'productInfo.sku');
  const tags = get(product, 'productInfo.tags');
  const dataFlashSale = get(product, 'flashSales.0');
  const { promotions, totalAvailable, productInfo } = product;
  const gifts = [];
  let tag: TagProps = null;
  const price = convertPrice(product);
  const isOutOfStock = totalAvailable === 0;
  if (isOutOfStock) {
    tag = {
      variant: 'link',
      children: t('Hàng sắp về'),
    };
  }
  if (!price?.price) {
    tag = {
      variant: '#4F49D9',
      children: t('Liên hệ'),
    };
  }
  const optionPromotions = promotions.filter(
    (el: any) =>
      !el.isDefault && el.applyOn === 'product' && !el?.condition?.coupon,
  );
  let addToCart;
  if (cart) {
    const orderLines = get(cart, 'order.orderLines', []);
    const currentOrderLine = orderLines.find(
      (orderline: CartOrderLine) => orderline.sku === productInfo.sku,
    );
    const optionPromotions = promotions.filter(
      (el: any) => !el.isDefault && el.applyOn === 'product',
    );
    const quantityInCart = (currentOrderLine && currentOrderLine.quantity) || 0;
    addToCart = {
      onAddToCart: async () => {
        await cart.onAddToCart(
          {
            sku: productInfo.sku,
            quantity: 1,
            selectPromotionId: optionPromotions[0]?.id,
          },
          true,
        );
        cart.refetch();
      },
      onIncrease: async () => {
        await cart.onIncrease(currentOrderLine);
      },
      onDecrease: async () => {
        if (quantityInCart < 2) {
          await cart.onRemoveItem(currentOrderLine);
        } else {
          await cart.onDecrease(currentOrderLine);
        }
        cart.refetch();
      },
      onChangeQuantity: async (newQuantity: number) => {
        await cart.onChangeQuantity(currentOrderLine, newQuantity);
      },
      quantityInCart,
    };
  }
  promotions.forEach((el: any) => {
    if (
      el.isDefault &&
      el.applyOn === 'product' &&
      (el?.benefit?.gifts || []).length
    ) {
      gifts.push(
        ...(el?.benefit?.gifts || []).map((item: any) => ({
          src: item.imageUrl,
          alt: item.name,
          sku: item.sku,
        })),
      );
    }
  });
  if (
    optionPromotions.length &&
    optionPromotions[0] &&
    (optionPromotions[0].benefit?.gifts || []).length
  ) {
    gifts.push(
      ...(optionPromotions[0].benefit?.gifts || []).map((item: any) => ({
        src: item.imageUrl,
        alt: item.name,
        sku: item.sku,
      })),
    );
  }
  return {
    name: get(product, 'productInfo.name'),
    frame: frames && getFrame(tags || [], frames),
    image: {
      src: get(product, 'productInfo.imageUrl'),
    },
    price,
    sku: sku,
    link: {
      as: getProductUrl(productInfo.name, productInfo.sku),
      href: { pathname: '/products/[sku]', query: { sku: sku } },
      passHref: true,
    },
    flashsale: dataFlashSale && {
      sold: dataFlashSale.usedCount,
      total: dataFlashSale.totalCount,
    },
    hasGift: !!gifts.length,
    tag,
    needConfirmAge: needConfirm18YearsOld(product?.isAlcoholicCategory),
    stockAvailable: totalAvailable === null ? 999 : totalAvailable,
    showContact:
      isOutOfStock || !isValidPrice(product) || !product?.status?.sellable,
    addToCart,
  };
};
type Location = {
  code: string;
  name: string;
};
export const getLimitShippingLocations = (
  providerId: number,
): Location[] | undefined => {
  const LIMIT_PROVIDERS = [
    {
      id: 100039,
      name: 'Meiji',
      locations: [
        { code: '01', name: t('Hà Nội') },
        { code: '79', name: t('Hồ Chí Minh') },
      ],
    },
    {
      id: 100041,
      name: t('Tân Hiệp Phát'),
      locations: [
        { code: '01', name: t('Hà Nội') },
        { code: '79', name: t('Hồ Chí Minh') },
      ],
    },
  ];
  return (LIMIT_PROVIDERS.find((provider) => provider.id === providerId) || {})
    .locations;
};
export const isDisplayBrandByTenant = (tenant: string): boolean => {
  switch (tenant) {
    case 'takashimaya':
      return true;
    default:
      return false;
  }
};

export const isDisplayUomByTenant = (tenant: string): boolean => {
  switch (tenant) {
    case 'soibien':
      return true;
    default:
      return false;
  }
};
